// UserName.jsx - Shared component for displaying user name with optional emblem
import React, { useEffect, useRef } from 'react';
import anime from 'animejs';

export const UserName = ({
  size = '',
  name = '',
  nameStyle = '',
  verification = false,
  verificationFilter = '',
  hide = false,
  verificationStyle = 'drop-shadow-xl',
  // Support both emblemUrl (direct URL) and emblem (for legacy/Meteor usage)
  emblemUrl = null,
  emblem = false,
  truncate = '',
  emblemStyle = 'drop-shadow-xl',
  addStyle = '',
  canChangeUsername = true,
  nextChangeDate = null,
  // Render prop for custom emblem rendering (used by Meteor app)
  renderEmblem = null,
  ...props
}) => {
  const verificationRef = useRef(null);
  const emblemRef = useRef(null);

  const hasEmblem = emblemUrl || emblem;

  useEffect(() => {
    if (verificationRef.current) {
      anime({
        targets: verificationRef.current,
        opacity: [0, 1],
        duration: 500,
        delay: 0,
        easing: 'easeOutSine',
      });
    }

    if (emblemRef.current) {
      anime({
        targets: emblemRef.current,
        opacity: [0, 1],
        duration: 500,
        delay: 0,
        easing: 'easeOutSine',
      });
    }
  }, [verification, hasEmblem]);

  const mediaSize =
    size === 'large'
      ? 'size-6'
      : size === 'wall'
        ? 'size-6 group-[.templateWall]/templateWall:xs:size-[4dvw] transition-all'
        : 'size-4';

  const textSize =
    size === 'large'
      ? 'text-xl leading-7'
      : size === 'wall'
        ? 'text-lg leading-[1.1] group-[.templateWall]/templateWall:xs:text-[5dvw] transition-all'
        : 'text-lg leading-6';

  // Render emblem - either via render prop, URL, or skip
  const renderEmblemElement = () => {
    if (renderEmblem && emblem) {
      // Use render prop for custom emblem rendering (Meteor's SingleImage)
      return renderEmblem(emblem, [mediaSize, emblemStyle].join(' '));
    }
    if (emblemUrl) {
      // Direct URL rendering for replay
      return (
        <img
          src={emblemUrl}
          className={[mediaSize, emblemStyle].join(' ')}
          alt=""
        />
      );
    }
    return null;
  };

  return (
    <div
      className={[
        'group/userName flex max-w-full min-w-0 items-center gap-1.5 group-[.templateWall]/templateWall:xs:gap-4',
        addStyle,
      ].join(' ')}
    >
      {hasEmblem && (
        <div ref={emblemRef} className="shrink-0">
          {renderEmblemElement()}
        </div>
      )}
      {verification && (
        <div ref={verificationRef} className="shrink-0">
          <img
            className={[mediaSize, verificationFilter, verificationStyle].join(
              ' '
            )}
            src={'/media/icons/ph-fill/seal-check-fill.svg'}
            alt=""
          />
        </div>
      )}
      {name && (
        <h4
          className={[
            textSize,
            nameStyle,
            truncate,
            !canChangeUsername && nextChangeDate ? 'cursor-help' : '',
          ].join(' ')}
          title={
            !canChangeUsername && nextChangeDate
              ? `You can change your username again at ${nextChangeDate.toLocaleDateString()}`
              : ''
          }
          {...props}
        >
          {!hide && name}
        </h4>
      )}
    </div>
  );
};
